// Ficha unica de un equipo de inventario: specs, edicion, bitacora y fichas
// de mantenimiento. Antes existian copias separadas (y ya distintas entre si)
// en Clientes.js y TecnicoPage.js. Los permisos (quien puede editar, eliminar
// o unificar) los decide quien monta este componente via props — el
// componente en si no conoce roles.
import { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { ModalMantenimiento } from '../pages/Clientes';
import {
  SelectorColorEquipo, LicenciaOfficeEditor, CorreoEquipoEditor, generarActaEntrega, imprimirInventarioEmpresa,
  exportarInventarioExcel, parsarDiscos, fmtMoney,
} from '../lib/equipos';

const fmtFecha = iso => iso ? new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtFechaHora = iso => iso ? new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const fechaInput = iso => iso ? String(iso).slice(0, 10) : '';

// Mueve UN equipo a otra empresa/cliente (conserva historial y mantenimientos).
function ModalMoverEquipo({ equipo, origen, onClose, onMovido }) {
  const [destino, setDestino] = useState('');
  const [opciones, setOpciones] = useState([]);
  const [msg, setMsg] = useState('');
  const [moviendo, setMoviendo] = useState(false);
  useEffect(() => {
    let vivo = true;
    Promise.all([
      API.get('/clientes').then(r => (Array.isArray(r.data) ? r.data.map(c => c.cliente) : [])).catch(() => []),
      API.get('/empresas-lista').then(r => (Array.isArray(r.data) ? r.data.map(c => c.nombre) : [])).catch(() => []),
    ]).then(([a, b]) => { if (vivo) setOpciones([...new Set([...a, ...b].filter(Boolean))].sort((x, y) => x.localeCompare(y, 'es'))); });
    return () => { vivo = false; };
  }, []);
  async function mover(e) {
    e.preventDefault();
    const d = destino.trim();
    if (!d) { setMsg('Escribe o elige la empresa destino'); return; }
    if (d === String(origen).trim()) { setMsg('El equipo ya esta en esa empresa'); return; }
    setMoviendo(true); setMsg('');
    try {
      const r = await API.post(`/tecnico/inventario/${equipo.id}/mover`, { empresa_destino: d });
      onMovido(r.data?.equipo, d);
    } catch (err) {
      setMsg(err?.response?.data?.error || 'No se pudo mover el equipo');
    } finally { setMoviendo(false); }
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <form onSubmit={mover} style={{ background: '#fff', borderRadius: 4, padding: 24, width: '100%', maxWidth: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#08293f', marginBottom: 6 }}>Mover equipo a otra empresa</div>
        <div style={{ fontSize: 12, color: '#5a7a8a', marginBottom: 14 }}>
          <strong>{equipo.nombre_equipo || equipo.identidad_valor}</strong> pasa de <strong>{origen}</strong> a la empresa que elijas. Se conservan su historial y sus mantenimientos, y queda anotado el traslado.
        </div>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#5a7a8a', display: 'block', marginBottom: 4 }}>Empresa o cliente destino</label>
        <input className="input" list="mover-equipo-destinos" value={destino} onChange={e => setDestino(e.target.value)} placeholder="Escribe para buscar o crea uno nuevo" autoFocus style={{ background: '#f9fafb', border: '1px solid #e8eef2', marginBottom: 8 }} />
        <datalist id="mover-equipo-destinos">{opciones.filter(o => o !== origen).map(o => <option key={o} value={o} />)}</datalist>
        {msg && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{msg}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button type="submit" disabled={moviendo} className="btn-ui btn-ui--md btn-ui--primary">{moviendo ? 'Moviendo...' : 'Mover equipo'}</button>
          <button type="button" onClick={onClose} className="btn-ui btn-ui--md btn-ui--outline">Cancelar</button>
        </div>
      </form>
    </div>
  );
}

export default function FichaEquipo({ equipo, nombre, onClose, onUnificar, onRecargar, canDelete = false, onEliminar, canMover = false }) {
  const [equipoActual, setEquipoActual] = useState(equipo);
  const [eventos,   setEventos]  = useState([]);
  const [cargando,  setCargando] = useState(true);
  const [motivo,    setMotivo]   = useState('');
  const [obs,       setObs]      = useState('');
  const [guardando, setGuardando]= useState(false);
  const [msg,       setMsg]      = useState('');
  const [editando, setEditando] = useState(false);
  const [formEquipo, setFormEquipo] = useState({});
  const [guardandoEquipo, setGuardandoEquipo] = useState(false);
  const [editMsg, setEditMsg] = useState('');
  const [infoEmpresa, setInfoEmpresa] = useState({});
  const [mantenimientos, setMantenimientos] = useState([]);
  const [showModalMant, setShowModalMant] = useState(false);
  const [cargandoMant, setCargandoMant] = useState(false);
  const [tab, setTab] = useState('ficha'); // ficha (por defecto) | bitacora | mantenimientos
  const [menuAcciones, setMenuAcciones] = useState(false);
  const [fotosCache, setFotosCache] = useState({});
  const [eliminando, setEliminando] = useState(false);
  const [moviendoEquipo, setMoviendoEquipo] = useState(false);
  const origen = 'tecnico';
  const eventosPath = `/tecnico/inventario/${equipoActual.id}/eventos`;

  const cargarEventos = useCallback(() => {
    setCargando(true);
    API.get(eventosPath)
      .then(r => { setEventos(r.data); setCargando(false); })
      .catch(() => setCargando(false));
  }, [eventosPath]);

  useEffect(() => { cargarEventos(); }, [cargarEventos]);

  const cargarMantenimientos = useCallback(() => {
    setCargandoMant(true);
    API.get(`/inventario/mantenimientos/${origen}/${equipoActual.id}`)
      .then(r => { setMantenimientos(Array.isArray(r.data) ? r.data : []); setCargandoMant(false); })
      .catch(() => setCargandoMant(false));
  }, [equipoActual.id]);
  useEffect(() => { cargarMantenimientos(); }, [cargarMantenimientos]);

  const cargarFotos = async (mantId) => {
    if (fotosCache[mantId]) return;
    try {
      const r = await API.get(`/inventario/mantenimientos/${origen}/${equipoActual.id}/${mantId}/fotos`);
      setFotosCache(prev => ({ ...prev, [mantId]: r.data }));
    } catch {}
  };

  useEffect(() => {
    API.get(`/clientes/${encodeURIComponent(nombre)}/info`)
      .then(r => setInfoEmpresa(r.data || {}))
      .catch(() => setInfoEmpresa({}));
  }, [nombre]);
  useEffect(() => {
    setEquipoActual(equipo);
    setFormEquipo({
      nombre_equipo: equipo.nombre_equipo || '',
      responsable: equipo.responsable || '',
      area: equipo.area || '',
      usuario_windows: equipo.usuario_windows || '',
      windows: equipo.windows || '',
      windows_version: equipo.windows_version || '',
      procesador: equipo.procesador || '',
      memoria_ram_gb: equipo.memoria_ram_gb || '',
      tarjeta_video: equipo.tarjeta_video || '',
      discos: equipo.discos || '',
      serial: equipo.serial || '',
      mac_wifi: equipo.mac_wifi || '',
      mac_ethernet: equipo.mac_ethernet || '',
      ultimo_mantenimiento: fechaInput(equipo.ultimo_mantenimiento),
      valor_comercial: equipo.valor_comercial || '',
      office_version: equipo.office_version || '',
      office_serial: equipo.office_serial || '',
    });
  }, [equipo]);

  async function agregar(e) {
    e.preventDefault();
    if (!motivo.trim() && !obs.trim()) return;
    setGuardando(true); setMsg('');
    try {
      await API.post(eventosPath, { motivo, observaciones: obs });
      setMotivo(''); setObs(''); setMsg('Guardado');
      cargarEventos();
      if (onRecargar) onRecargar();
    } catch (err) { setMsg(err?.response?.data?.error || 'Error'); }
    finally { setGuardando(false); }
  }

  const soStr = [equipoActual.windows, equipoActual.windows_version].filter(Boolean).join(' — ');
  const officeStr = equipoActual.office_version
    ? `Versión ${equipoActual.office_version}${equipoActual.office_serial ? ` — Serial: ${equipoActual.office_serial}` : ''}`
    : null;

  async function guardarEquipo(e) {
    e.preventDefault();
    setGuardandoEquipo(true);
    setEditMsg('');
    try {
      const payload = { ...formEquipo, memoria_ram_gb: formEquipo.memoria_ram_gb || null, valor_comercial: formEquipo.valor_comercial || null };
      const r = await API.put(`/tecnico/inventario/${equipoActual.id}`, payload);
      const actualizado = { ...equipoActual, ...(r.data?.equipo || payload), origen_inventario: 'tecnico' };
      setEquipoActual(actualizado);
      setEditando(false);
      setEditMsg('Guardado');
      if (onRecargar) onRecargar();
    } catch (err) {
      setEditMsg(err?.response?.data?.error || 'No se pudo guardar');
    } finally {
      setGuardandoEquipo(false);
    }
  }

  async function cambiarClasificacion(clasificacion_color) {
    try {
      await API.put(`/inventario/clasificacion/tecnico/${equipoActual.id}`, { clasificacion_color });
      setEquipoActual(actual => ({ ...actual, clasificacion_color }));
      if (onRecargar) onRecargar();
    } catch (err) {
      setEditMsg(err?.response?.data?.error || 'No se pudo guardar el color');
    }
  }

  async function guardarOffice(payload) {
    const r = await API.put(`/inventario/office/${equipoActual.id}`, payload);
    setEquipoActual(actual => ({ ...actual, ...(r.data || {}) }));
    if (onRecargar) onRecargar();
  }

  async function guardarCorreo(correo_electronico) {
    const r = await API.put(`/inventario/correo/${equipoActual.id}`, { correo_electronico });
    setEquipoActual(actual => ({ ...actual, ...(r.data || {}) }));
    if (onRecargar) onRecargar();
  }

  async function eliminar() {
    if (!onEliminar) return;
    if (!window.confirm(`Eliminar ${equipoActual.nombre_equipo || 'este equipo'} del inventario?`)) return;
    setEliminando(true);
    try {
      await onEliminar(equipoActual);
    } finally {
      setEliminando(false);
    }
  }

  const editarCampo = (key, label, type = 'text') => (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7f9aad' }}>{label}</span>
      <input type={type} className="input" value={formEquipo[key] ?? ''} onChange={e => setFormEquipo(f => ({ ...f, [key]: e.target.value }))} style={{ background: '#f9fafb', border: '1px solid #e8eef2', fontSize: 12 }} />
    </label>
  );

  const nombreEquipo = equipoActual.nombre_equipo || equipoActual.identidad_valor;
  const tituloFicha = `Ficha equipo - ${nombreEquipo}`;
  const fila = (label, valor, mono = false) => (
    <div className="fe-fila">
      <small>{label}</small>
      <span style={mono && valor ? { fontFamily: "'Consolas','Courier New',monospace", fontSize: 12 } : undefined} className={valor ? '' : 'fe-vacio'}>{valor || '—'}</span>
    </div>
  );
  const grupo = titulo => <div className="fe-grupo">{titulo}</div>;
  const pestana = (key, label, n) => (
    <button key={key} type="button" onClick={() => setTab(key)} style={{
      fontFamily: 'inherit', fontSize: 11, fontWeight: 700, padding: '6px 16px', borderRadius: 2, cursor: 'pointer',
      border: `1px solid ${tab === key ? '#08293f' : '#e8eef2'}`, background: tab === key ? '#08293f' : '#fff', color: tab === key ? '#00b8e3' : '#5a7a8a',
    }}>
      {label}{n != null && <span style={{ marginLeft: 6, fontSize: 10, padding: '0 6px', borderRadius: 8, background: tab === key ? 'rgba(0,184,227,0.2)' : '#eef3f6' }}>{n}</span>}
    </button>
  );
  const itemMenu = (label, onClick, extra = {}) => (
    <button type="button" onClick={() => { setMenuAcciones(false); onClick(); }} disabled={extra.disabled}
      style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '9px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', color: extra.color || '#08293f' }}>
      {label}
    </button>
  );

  return (
    <div>
      <style>{`
        .fe-fila { display: grid; grid-template-columns: 190px minmax(0, 1fr); gap: 12px; padding: 8px 14px; border-bottom: 1px solid #eef3f6; align-items: baseline; }
        .fe-fila:last-child { border-bottom: none; }
        .fe-fila small { font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: #5a7a8a; }
        .fe-fila span { font-size: 12.5px; font-weight: 600; color: #08293f; word-break: break-word; }
        .fe-fila span.fe-vacio { color: #8aa0ad; font-weight: 400; }
        .fe-grupo { font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #5a7a8a; background: #f9fafb; padding: 7px 14px; border-bottom: 1px solid #e8eef2; border-top: 1px solid #e8eef2; }
        .fe-grupo:first-child { border-top: none; }
        @media (max-width: 560px) { .fe-fila { grid-template-columns: 1fr; gap: 1px; } }
      `}</style>

      {/* cabecera: volver + acciones */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <button type="button" onClick={onClose} className="btn-ui btn-ui--outline">← Volver</button>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn-ui btn-ui--primary" onClick={() => generarActaEntrega({ empresa: nombre, infoEmpresa, equipo: equipoActual })}>Generar acta de entrega</button>
          <button type="button" className="btn-ui btn-ui--outline" onClick={() => setEditando(v => !v)}>{editando ? 'Cancelar edición' : 'Editar equipo'}</button>
          {(
            <div style={{ position: 'relative' }}>
              {menuAcciones && <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setMenuAcciones(false)} />}
              <button type="button" className="btn-ui btn-ui--outline" onClick={() => setMenuAcciones(v => !v)} aria-haspopup="menu" aria-expanded={menuAcciones}>Más acciones {menuAcciones ? '▲' : '▾'}</button>
              {menuAcciones && (
                <div role="menu" style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100, background: '#fff', border: '1px solid #dce6ef', borderRadius: 4, padding: '4px 0', minWidth: 210, boxShadow: '0 4px 14px rgba(0,0,0,0.14)' }}>
                  {itemMenu('Exportar PDF', () => imprimirInventarioEmpresa({ empresa: nombre, titulo: tituloFicha, equipos: [equipoActual], modo: 'fichas', infoEmpresa }))}
                  {itemMenu('Exportar Excel', () => exportarInventarioExcel({ nombre, titulo: tituloFicha, equipos: [equipoActual] }))}
                  {(canMover || onUnificar || (canDelete && onEliminar)) && <div style={{ borderTop: '1px solid #eef2f6', margin: '4px 0' }} />}
                  {canMover && itemMenu('Mover a otra empresa', () => setMoviendoEquipo(true))}
                  {onUnificar && itemMenu('Unificar con otro equipo', () => onUnificar(equipoActual), { color: '#b45309' })}
                  {canDelete && onEliminar && itemMenu(eliminando ? 'Eliminando...' : 'Eliminar equipo', eliminar, { color: '#dc2626', disabled: eliminando })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {moviendoEquipo && <ModalMoverEquipo equipo={equipoActual} origen={nombre} onClose={() => setMoviendoEquipo(false)} onMovido={(eq, d) => { setMoviendoEquipo(false); alert(`Equipo movido a ${d}`); if (onRecargar) onRecargar(); onClose(); }} />}

      {/* titulo y estado */}
      <div style={{ fontSize: 20, fontWeight: 600, color: '#08293f', marginBottom: 2, wordBreak: 'break-word' }}>{nombreEquipo}</div>
      <div style={{ fontSize: 12, color: '#5a7a8a', marginBottom: 14, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span>{nombre}</span>
        <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#0f766e', background: 'rgba(15,118,110,0.08)', border: '1px solid rgba(15,118,110,0.18)', padding: '2px 7px', borderRadius: 2 }}>Inventario</span>
        <SelectorColorEquipo equipo={equipoActual} onChange={cambiarClasificacion} />
        {equipoActual.responsable && <span>· Responsable: {equipoActual.responsable}</span>}
        {equipoActual.ultimo_evento_fecha && <span>· Última visita {fmtFecha(equipoActual.ultimo_evento_fecha)}</span>}
        {editMsg && <span style={{ fontSize: 11, fontWeight: 700, color: editMsg === 'Guardado' ? '#059669' : '#dc2626' }}>{editMsg}</span>}
      </div>

      {editando && (
        <form onSubmit={guardarEquipo} style={{ background: '#fff', border: '1px solid #d8e5ec', borderRadius: 2, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 10 }}>Editar inventario</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            {editarCampo('nombre_equipo', 'Equipo')}
            {editarCampo('responsable', 'Responsable')}
            {editarCampo('area', 'Area')}
            {editarCampo('usuario_windows', 'Usuario Windows')}
            {editarCampo('windows', 'Sistema operativo')}
            {editarCampo('windows_version', 'Version')}
            {editarCampo('procesador', 'Procesador')}
            {editarCampo('memoria_ram_gb', 'RAM GB', 'number')}
            {editarCampo('tarjeta_video', 'Tarjeta video')}
            {editarCampo('discos', 'Discos')}
            {editarCampo('serial', 'Serial')}
            {editarCampo('mac_wifi', 'MAC WiFi')}
            {editarCampo('mac_ethernet', 'MAC Ethernet')}
            {editarCampo('ultimo_mantenimiento', 'Ultimo mantenimiento', 'date')}
            {editarCampo('valor_comercial', 'Valor comercial', 'number')}
            {editarCampo('office_version', 'Office version')}
            {editarCampo('office_serial', 'Office serial')}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" disabled={guardandoEquipo} className="btn-ui btn-ui--md btn-ui--primary">{guardandoEquipo ? 'Guardando...' : 'Guardar cambios'}</button>
            <button type="button" onClick={() => setEditando(false)} className="btn-ui btn-ui--md btn-ui--outline">Cancelar</button>
          </div>
        </form>
      )}

      {/* pestañas */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
        {pestana('ficha', 'Ficha')}
        {pestana('bitacora', 'Bitácora', eventos.length)}
        {pestana('mantenimientos', 'Mantenimientos', mantenimientos.length)}
      </div>

      {tab === 'ficha' && (
        <div style={{ border: '1px solid #e8eef2', borderRadius: 4, overflow: 'hidden', background: '#fff' }}>
          {grupo('Identificación')}
          {fila('Cliente', nombre)}
          {fila('Responsable', equipoActual.responsable)}
          {fila('Área', equipoActual.area)}
          {fila('Serial', equipoActual.serial || (equipoActual.identidad_tipo !== 'serial' ? `Sin serial (id: ${equipoActual.identidad_tipo})` : null), true)}
          {fila('Identificación usada', equipoActual.identidad_tipo && equipoActual.identidad_valor ? `${equipoActual.identidad_tipo}: ${equipoActual.identidad_valor}` : null, true)}
          {grupo('Hardware')}
          {fila('Procesador', equipoActual.procesador)}
          {fila('RAM', equipoActual.memoria_ram_gb ? `${equipoActual.memoria_ram_gb} GB` : null)}
          {fila('Disco duro', parsarDiscos(equipoActual.discos) === '—' ? null : parsarDiscos(equipoActual.discos))}
          {fila('Tarjeta de video', equipoActual.tarjeta_video)}
          {fila('MAC WiFi', equipoActual.mac_wifi, true)}
          {fila('MAC Ethernet', equipoActual.mac_ethernet, true)}
          {grupo('Software')}
          {fila('Sistema operativo', soStr)}
          {fila('Usuario Windows', equipoActual.usuario_windows)}
          <CorreoEquipoEditor equipo={equipoActual} onGuardar={guardarCorreo} />
          {fila('Microsoft Office', officeStr)}
          <LicenciaOfficeEditor equipo={equipoActual} onGuardar={guardarOffice} />
          {grupo('Mantenimiento')}
          {fila('Última visita', equipoActual.ultimo_evento_fecha ? fmtFecha(equipoActual.ultimo_evento_fecha) : null)}
          {fila('Último mantenimiento', fechaInput(equipoActual.ultimo_mantenimiento) || null)}
          {fila('Valor comercial', equipoActual.valor_comercial ? fmtMoney(equipoActual.valor_comercial) : null)}
        </div>
      )}

      {tab === 'bitacora' && (
        <div>
          <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, padding: '14px 16px', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', marginBottom: 10 }}>— Agregar a bitácora</div>
            <form onSubmit={agregar} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <input className="input" value={motivo} onChange={e => setMotivo(e.target.value)} placeholder="Motivo / proceso" style={{ background: '#f9fafb', border: '1px solid #e8eef2', flex: '1 1 160px', minWidth: 0 }} />
              <input className="input" value={obs} onChange={e => setObs(e.target.value)} placeholder="Observaciones, incidencia o solución aplicada" style={{ background: '#f9fafb', border: '1px solid #e8eef2', flex: '3 1 240px', minWidth: 0 }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <button type="submit" disabled={guardando} className="btn-ui btn-ui--md btn-ui--primary">Agregar</button>
                {msg && <span style={{ fontSize: 11, color: msg === 'Guardado' ? '#059669' : '#dc2626', fontWeight: 600 }}>{msg}</span>}
              </div>
            </form>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a', padding: '12px 16px' }}>— Bitácora de intervenciones ({eventos.length})</div>
            {cargando && <div style={{ padding: '20px 16px', color: '#b3b3b3', fontSize: 12 }}>Cargando...</div>}
            {!cargando && eventos.length === 0 && <div style={{ padding: '20px 16px', color: '#b3b3b3', fontSize: 12 }}>Sin intervenciones registradas</div>}
            {eventos.map((ev, i) => (
              <div key={ev.id} style={{ padding: '12px 16px', borderTop: i === 0 ? '1px solid #e8eef2' : '1px solid #f4f7fa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#08293f' }}>{ev.motivo || 'Sin motivo'}</div>
                  <div style={{ fontSize: 10, color: '#b3b3b3', whiteSpace: 'nowrap' }}>
                    {fmtFechaHora(ev.created_at)}{ev.usuario_nombre ? ` · ${ev.usuario_nombre}` : ''}
                    {' · '}<span style={{ color: ev.origen === 'script' ? '#00b8e3' : '#5a7a8a' }}>{ev.origen === 'script' ? 'Script' : 'Manual'}</span>
                  </div>
                </div>
                {ev.observaciones && <div style={{ fontSize: 11, color: '#5a7a8a', marginBottom: 4 }}>{ev.observaciones}</div>}
                {ev.cambios && (() => { try { const c = JSON.parse(ev.cambios); const ks = Object.keys(c).filter(k => k !== 'nuevo'); if (!ks.length) return null; return (
                  <div style={{ marginTop: 4, fontSize: 10, color: '#b45309', background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)', borderRadius: 2, padding: '4px 8px' }}>
                    {c.nuevo ? 'Primer registro' : ks.map(k => `${k}: ${c[k].antes ?? '—'} → ${c[k].despues ?? '—'}`).join(' | ')}
                  </div>
                ); } catch { return null; } })()}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'mantenimientos' && (
        <div style={{ background: '#fff', border: '1px solid #e8eef2', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#5a7a8a' }}>
              — Fichas de mantenimiento {mantenimientos.length > 0 ? `(${mantenimientos.length})` : ''}
            </div>
            <button type="button" onClick={() => setShowModalMant(true)} className="btn-ui btn-ui--dark">+ Nueva ficha</button>
          </div>
          <div>
            {cargandoMant && <div style={{ padding: '16px', color: '#b3b3b3', fontSize: 12 }}>Cargando...</div>}
            {!cargandoMant && mantenimientos.length === 0 && (
              <div style={{ padding: '20px 16px', color: '#b3b3b3', fontSize: 12, textAlign: 'center' }}>Sin fichas de mantenimiento registradas</div>
            )}
            {mantenimientos.map(m => {
              const TIPO_LABEL = { preventivo: 'Preventivo', correctivo: 'Correctivo', verificacion: 'Verificacion', ambos: 'Prev + Correc' };
              const TIPO_COLOR = { preventivo: '#059669', correctivo: '#dc2626', verificacion: '#6366f1', ambos: '#b45309' };
              const tc = TIPO_COLOR[m.tipo_solicitud] || '#5a7a8a';
              const fotos = fotosCache[m.id];
              const inspeccionados = [
                m.insp_case, m.insp_puertos_usb, m.insp_teclado_mouse,
                m.insp_bateria_cargador, m.insp_adaptador_energia, m.insp_pantalla,
              ].filter(Boolean);
              return (
                <div key={m.id} style={{ borderTop: '1px solid #f0f4f8' }}>
                  <div style={{ background: '#f9fafb', padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, borderBottom: '1px solid #f0f4f8' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 900, color: '#08293f' }}>Reporte #{m.numero_reporte}</span>
                      <span style={{ fontSize: 11, color: '#5a7a8a' }}>{m.fecha?.slice(0,10)}</span>
                      <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 20, background: `${tc}15`, color: tc, border: `1px solid ${tc}30` }}>
                        {TIPO_LABEL[m.tipo_solicitud] || m.tipo_solicitud}
                      </span>
                      <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                        background: m.estado_inicial === 'funcional' ? 'rgba(5,150,105,0.08)' : 'rgba(220,38,38,0.08)',
                        color: m.estado_inicial === 'funcional' ? '#059669' : '#dc2626',
                        border: `1px solid ${m.estado_inicial === 'funcional' ? 'rgba(5,150,105,0.2)' : 'rgba(220,38,38,0.2)'}` }}>
                        {m.estado_inicial === 'funcional' ? 'Funcional' : m.estado_inicial === 'no_enciende' ? 'No encendia' : m.estado_inicial_otro || m.estado_inicial}
                      </span>
                    </div>
                    <span style={{ fontSize: 10, color: '#b3b3b3', whiteSpace: 'nowrap', textAlign: 'right' }}>{m.responsable?.split('—')[0]?.trim() || m.responsable?.split('\n')[0] || ''}</span>
                  </div>
                  <div style={{ padding: '10px 16px' }}>
                    {m.actividades && (
                      <div style={{ fontSize: 11, color: '#5a7a8a', lineHeight: 1.6, whiteSpace: 'pre-line', marginBottom: 6 }}>{m.actividades}</div>
                    )}
                    {inspeccionados.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                        {[['Case','insp_case'],['USB','insp_puertos_usb'],['Teclado','insp_teclado_mouse'],
                          ['Bateria','insp_bateria_cargador'],['Adaptador','insp_adaptador_energia'],['Pantalla','insp_pantalla']
                        ].filter(([,k]) => m[k]).map(([label, k]) => (
                          <span key={k} style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 2,
                            background: m[k] === 'B' ? 'rgba(5,150,105,0.08)' : m[k] === 'M' ? 'rgba(220,38,38,0.08)' : 'rgba(251,191,36,0.1)',
                            color: m[k] === 'B' ? '#059669' : m[k] === 'M' ? '#dc2626' : '#b45309',
                            border: `1px solid ${m[k] === 'B' ? 'rgba(5,150,105,0.2)' : m[k] === 'M' ? 'rgba(220,38,38,0.2)' : 'rgba(251,191,36,0.25)'}` }}>
                            {label}: {m[k]}
                          </span>
                        ))}
                      </div>
                    )}
                    {m.observaciones && (
                      <div style={{ fontSize: 11, color: '#b45309', fontStyle: 'italic', borderLeft: '2px solid #f59e0b', paddingLeft: 8, marginBottom: 6 }}>{m.observaciones}</div>
                    )}
                    {(m.has_foto1 || m.has_foto2) && (
                      <div style={{ marginTop: 4 }}>
                        {!fotos ? (
                          <button onClick={() => cargarFotos(m.id)}
                            style={{ fontSize: 11, fontWeight: 700, color: '#00b8e3', background: 'rgba(0,184,227,0.07)', border: '1px solid rgba(0,184,227,0.2)', borderRadius: 3, padding: '3px 10px', cursor: 'pointer' }}>
                            📷 Ver evidencia ({[m.has_foto1, m.has_foto2].filter(Boolean).length} foto{[m.has_foto1, m.has_foto2].filter(Boolean).length > 1 ? 's' : ''})
                          </button>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {[fotos.foto1, fotos.foto2].filter(Boolean).map((f, i) => (
                              <a key={i} href={f} target="_blank" rel="noopener noreferrer"
                                style={{ display: 'block', borderRadius: 4, overflow: 'hidden', border: '1px solid #d8e5ec' }}>
                                <img src={f} alt={`foto ${i+1}`} style={{ height: 80, display: 'block', cursor: 'zoom-in' }} />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showModalMant && (
        <ModalMantenimiento
          equipo={equipoActual} nombre={nombre} origen={origen}
          onClose={() => setShowModalMant(false)}
          onGuardado={() => { cargarMantenimientos(); if (onRecargar) onRecargar(); }}
        />
      )}
    </div>
  );
}
